import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RefreshCw, Loader2, ArrowLeft } from 'lucide-react';
import { MessageList } from '../components/ai/MessageList';
import { ChatInput } from '../components/ai/ChatInput';

export default function AIAssistant() {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(true);
  const [briefingMode, setBriefingMode] = useState('auto');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchBriefingAndSettings = useCallback(async () => {
    setBriefingLoading(true);
    try {
      // Fetch settings first
      const settingsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        credentials: 'include'
      });
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setBriefingMode(settingsData.briefing_mode);
      }

      // Fetch briefing
      const briefingRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/briefing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ force: false })
      });
      if (briefingRes.ok) {
        const briefingData = await briefingRes.json();
        setBriefing(briefingData);
      }
    } catch (error) {
      console.error('Failed to fetch briefing:', error);
    } finally {
      setBriefingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBriefingAndSettings();
  }, [fetchBriefingAndSettings]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleRefreshBriefing = useCallback(async () => {
    setBriefingLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/briefing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ force: true })
      });
      if (response.ok) {
        const data = await response.json();
        setBriefing(data);
      }
    } catch (error) {
      console.error('Failed to refresh briefing:', error);
    } finally {
      setBriefingLoading(false);
    }
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || streaming) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setStreaming(true);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      // Add empty assistant message
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              break;
            }
            assistantMessage += data;
            // Update the last message
            setMessages((prev) => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: 'assistant',
                content: assistantMessage
              };
              return newMessages;
            });
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted by user
      } else {
        console.error('Chat failed:', error);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }
        ]);
      }
    } finally {
      setStreaming(false);
      abortControllerRef.current = null;
    }
  }, [inputValue, streaming]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedPrompts = [
    "What's on my schedule this week?",
    "Show me unpaid invoices",
    "Any unsigned contracts?"
  ];

  return (
    <div className="min-h-screen bg-background pb-32 pt-4 px-4 flex flex-col">
      <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
            aria-label="Back to dashboard"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-text-muted" />
          </button>
          <Sparkles className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">AI Assistant</h1>
        </div>

        {/* Daily Briefing Card */}
        <div className="bg-surface rounded-lg border border-border p-6 mb-6" data-testid="daily-briefing-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium text-foreground">Daily Briefing</h2>
            {briefingMode === 'manual' && (
              <button
                onClick={handleRefreshBriefing}
                disabled={briefingLoading}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-surface-hover rounded-lg transition-colors disabled:opacity-50"
                data-testid="refresh-briefing-btn"
              >
                <RefreshCw className={`w-4 h-4 ${briefingLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
          </div>

          {briefingLoading ? (
            <div className="flex items-center gap-2 text-text-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Generating briefing...</span>
            </div>
          ) : briefing ? (
            <div>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {briefing.briefing}
              </p>
              {briefing.action_items && briefing.action_items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h3 className="text-sm font-medium text-foreground mb-2">Action Items:</h3>
                  <ul className="space-y-1">
                    {briefing.action_items.map((item, idx) => (
                      <li key={`action-item-${idx}-${item.substring(0, 20)}`} className="text-sm text-text-muted flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {briefing.cached && (
                <p className="text-xs text-text-muted mt-3">
                  Cached from {new Date(briefing.generated_at).toLocaleTimeString()}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-text-muted">Failed to load briefing</p>
          )}
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4" data-testid="chat-messages">
          <MessageList
            messages={messages}
            streaming={streaming}
            suggestedPrompts={suggestedPrompts}
            onPromptClick={setInputValue}
            messagesEndRef={messagesEndRef}
          />
        </div>

        {/* Input Bar */}
        <ChatInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onSend={handleSendMessage}
          disabled={streaming}
          onKeyPress={handleKeyPress}
        />
      </div>
    </div>
  );
}
